import React, { useState, useEffect } from 'react';
import { Section, ToolbarButton } from './UI';
import { PromptEditor } from './PromptEditor';
import { promptsService } from '../services/promptsService';
import { useToast } from './Toast';

/**
 * Страница управления AI промптами (только для super-admin)
 */
export function PromptsManagement({ setView }) {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [debugInfo, setDebugInfo] = useState('Initializing...'); // Для отладки
  const toast = useToast();

  /**
   * Загрузить все промпты из API
   */
  const loadPrompts = async () => {
    console.log('🔥 loadPrompts STARTED');
    setDebugInfo('loadPrompts STARTED');
    
    try {
      setLoading(true);
      console.log('🔄 Starting to load prompts...');
      setDebugInfo('Calling API...');
      
      const data = await promptsService.getAllPrompts();
      console.log('📥 Received data from API:', data);
      setDebugInfo(`Received ${Array.isArray(data) ? data.length : 0} prompts`);
      console.log('🔍 Data type:', typeof data, 'Is array:', Array.isArray(data));
      
      // Добавляем валидацию данных
      if (!Array.isArray(data)) {
        console.error('❌ Некорректный формат данных промптов:', data);
        setDebugInfo('ERROR: Data is not array');
        toast.error('Получены некорректные данные от сервера');
        setPrompts([]);
        setLoading(false);
        return;
      }
      
      console.log(`📋 Data is array with ${data.length} items`);
      setDebugInfo(`Validating ${data.length} prompts...`);
      
      // Проверяем каждый промпт на корректность
      const validPrompts = data.filter((p, idx) => {
        console.log(`🔎 Validating prompt ${idx}:`, p);
        if (!p || typeof p !== 'object') {
          console.error(`❌ Некорректный промпт ${idx}:`, p);
          return false;
        }
        if (!p.id || !p.prompt_type || !p.default_prompt) {
          console.error(`❌ Промпт ${idx} с отсутствующими обязательными полями:`, p);
          console.error(`  - has id: ${!!p.id}, has prompt_type: ${!!p.prompt_type}, has default_prompt: ${!!p.default_prompt}`);
          return false;
        }
        console.log(`✅ Prompt ${idx} is valid`);
        return true;
      });
      
      console.log(`✅ Загружено промптов: ${validPrompts.length}`, validPrompts);
      setDebugInfo(`Loaded ${validPrompts.length} valid prompts`);
      setPrompts(validPrompts);
      setLoading(false);
      console.log('✅ loadPrompts COMPLETED, loading=false');
      setDebugInfo('SUCCESS');
    } catch (error) {
      console.error('❌ Ошибка загрузки промптов:', error);
      console.error('Error stack:', error.stack);
      setDebugInfo(`ERROR: ${error.message}`);
      toast.error('Не удалось загрузить промпты: ' + (error.message || 'Неизвестная ошибка'));
      setPrompts([]);
      setLoading(false);
    } finally {
      console.log('⚙️ Loading finished (finally block)');
    }
  };

  // Загрузка промптов при монтировании
  useEffect(() => {
    console.log('🎯 PromptsManagement useEffect triggered - calling loadPrompts');
    loadPrompts();
  }, []);

  /**
   * Сохранить изменения промпта
   */
  const handleSavePrompt = async (promptType, data) => {
    console.log('💾 handleSavePrompt called:', { promptType, data });
    
    try {
      setSaving(true);
      
      console.log('📤 Sending update request...');
      const updated = await promptsService.updatePrompt(promptType, data);
      
      console.log('✅ Update successful, received:', updated);
      console.log('🔍 Type:', typeof updated, 'Has prompt_type:', !!updated?.prompt_type);
      
      // Обновляем локальное состояние
      setPrompts(prev => {
        const newPrompts = prev.map(p => 
          p.prompt_type === promptType ? updated : p
        );
        console.log('🔄 Updated prompts state:', newPrompts.length, 'items');
        return newPrompts;
      });
      
      toast.success('Промпт успешно обновлен!');
    } catch (error) {
      console.error('❌ Ошибка сохранения промпта:', error);
      console.error('Error details:', error.message, error.stack);
      toast.error(error.response?.data?.detail || 'Ошибка сохранения промпта');
    } finally {
      setSaving(false);
      console.log('⚙️ Save operation finished');
    }
  };

  /**
   * Сбросить промпт к дефолтному
   */
  const handleResetPrompt = async (promptType) => {
    try {
      setSaving(true);
      const reset = await promptsService.resetPrompt(promptType);
      
      // Обновляем локальное состояние
      setPrompts(prev => prev.map(p => 
        p.prompt_type === promptType ? reset : p
      ));
      
      toast.success('Промпт сброшен к оригинальному значению');
    } catch (error) {
      console.error('Ошибка сброса промпта:', error);
      toast.error(error.response?.data?.detail || 'Ошибка сброса промпта');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Section 
        title="🤖 Управление AI Промптами" 
        right={<ToolbarButton onClick={() => setView('home')}>Меню</ToolbarButton>}
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-center space-y-3">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
            <p className="text-gray-600 dark:text-gray-400">Загрузка промптов...</p>
            <p className="text-xs text-gray-500 dark:text-gray-500">Debug: {debugInfo}</p>
          </div>
        </div>
      </Section>
    );
  }

  return (
    <Section 
      title="🤖 Управление AI Промптами" 
      right={<ToolbarButton onClick={() => setView('home')}>Меню</ToolbarButton>}
    >
      <div className="space-y-6">
        {/* Описание */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
            ℹ️ О системе промптов
          </h3>
          <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-disc pl-5">
            <li>Здесь можно настроить промпты для AI обработки аудио, текста и фото</li>
            <li>Оригинальные промпты всегда сохраняются и доступны для восстановления</li>
            <li>Изменения применяются сразу после сохранения</li>
            <li>Чекбокс "Использовать мой промпт" включает/выключает кастомный промпт</li>
          </ul>
        </div>

        {/* Список промптов */}
        {prompts.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <p>Промпты не найдены</p>
          </div>
        ) : (
          <div className="space-y-6">
            {prompts.map((prompt) => {
              // Дополнительная проверка перед рендером
              if (!prompt || !prompt.id) {
                console.error('Попытка отрендерить некорректный промпт:', prompt);
                return null;
              }
              
              return (
                <PromptEditor
                  key={prompt.id}
                  prompt={prompt}
                  onSave={handleSavePrompt}
                  onReset={handleResetPrompt}
                  disabled={saving}
                />
              );
            })}
          </div>
        )}

        {/* Кнопка обновления */}
        <div className="flex justify-center pt-4">
          <button
            type="button"
            onClick={loadPrompts}
            disabled={loading || saving}
            className="px-6 py-2 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            🔄 Обновить список
          </button>
        </div>
      </div>
    </Section>
  );
}
